// Product videos: checked and compressed in the staff member's browser (the server is too small to transcode).
// Output: portrait MP4, H.264 + AAC, at most 1080 wide, about 5 Mbps, moov first (starts quickly), plus a JPEG cover
// frame. mediabunny (WebCodecs, hardware encoder) is loaded only when staff pick a video.

export const MAX_VIDEO_SECONDS = 60
const MAX_SOURCE_BYTES = 1024 * 1024 * 1024 // 1 GB original from the phone
const MAX_WIDTH = 1080
const MAX_FPS = 30

export const VIDEO_MESSAGES = {
    notVideo: 'Please choose a video file.',
    tooBig: 'This video is over 1 GB. Please trim it on your phone first.',
    tooLong: `Videos can be up to ${MAX_VIDEO_SECONDS} seconds. Trim it on your phone, then upload it again.`,
    notPortrait: 'Please record the video vertically (portrait).',
    unreadable: "We couldn't read this video. Please try another one.",
    unsupported: "This browser can't prepare videos. Use Chrome or Edge, or Safari on iPhone (iOS 17 or later).",
    failed: "We couldn't prepare this video. Please try again or choose another one.",
}

const load = () => import('mediabunny')

const fail = (message) => Object.assign(new Error(message), { friendly: true })

const even = (n) => Math.max(2, Math.round(n / 2) * 2)

export const canCompressVideo = () => typeof window !== 'undefined' && typeof window.VideoEncoder === 'function'
    && typeof window.VideoDecoder === 'function'

// { duration (s), width, height (as shown, after rotation) }; throws an Error with a friendly message
export const inspectVideo = async (file) => {
    // some phones send no type for .mov / .3gp files, so the extension also counts
    const looksLikeVideo = file && ((file.type || '').startsWith('video/') || /\.(mp4|mov|m4v|webm|3gp|mkv)$/i.test(file.name || ''))
    if (!looksLikeVideo) {
        throw fail(VIDEO_MESSAGES.notVideo)
    }
    if (file.size > MAX_SOURCE_BYTES) throw fail(VIDEO_MESSAGES.tooBig)
    if (!canCompressVideo()) throw fail(VIDEO_MESSAGES.unsupported)

    const { Input, BlobSource, ALL_FORMATS } = await load()
    const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS })
    try {
        const track = await input.getPrimaryVideoTrack()
        if (!track) throw fail(VIDEO_MESSAGES.unreadable)
        const duration = await input.computeDuration()
        const info = { duration, width: track.displayWidth, height: track.displayHeight }
        if (duration > MAX_VIDEO_SECONDS + 0.5) throw fail(VIDEO_MESSAGES.tooLong)
        if (!(info.height > info.width)) throw fail(VIDEO_MESSAGES.notPortrait)
        if (!(await track.canDecode())) throw fail(VIDEO_MESSAGES.unsupported)
        return info
    } catch (error) {
        if (error.friendly) throw error
        throw fail(VIDEO_MESSAGES.unreadable)
    } finally {
        input.dispose()
    }
}

// Compresses to the shop's video format. onProgress(0..1); signal (AbortSignal) cancels.
// Returns { blob (video/mp4), poster (image/jpeg), duration, width, height }
export const compressVideo = async (file, onProgress, signal) => {
    const mb = await load()
    const { Input, BlobSource, ALL_FORMATS, Output, Mp4OutputFormat, BufferTarget, Conversion, CanvasSink } = mb
    const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS })
    try {
        const track = await input.getPrimaryVideoTrack()
        if (!track) throw fail(VIDEO_MESSAGES.unreadable)
        const width = even(Math.min(MAX_WIDTH, track.displayWidth))
        const height = even(track.displayHeight * (width / track.displayWidth))
        // about Reels quality: ~5 Mbps at 1080x1920, less for smaller videos
        const bitrate = Math.round(Math.min(5_000_000, Math.max(1_500_000, width * height * 2.4)))
        const stats = await track.computePacketStats(60).catch(() => null)
        const frameRate = stats && stats.averagePacketRate > MAX_FPS + 1 ? MAX_FPS : undefined

        const codec = await mb.getFirstEncodableVideoCodec(['avc'], { width, height })
        if (!codec) throw fail(VIDEO_MESSAGES.unsupported)

        const target = new BufferTarget()
        const output = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }), target })
        const conversion = await Conversion.init({
            input,
            output,
            copy: false, // always re-encode, so every video has the same codec, size and bitrate
            video: { codec: 'avc', width, height, fit: 'fill', bitrate, frameRate, keyFrameInterval: 2 },
            audio: { codec: 'aac', bitrate: 128_000 },
            trim: { end: MAX_VIDEO_SECONDS },
            showWarnings: false,
        })
        if (!conversion.isValid) {
            const videoDropped = conversion.discardedTracks.some(t => t.track.isVideoTrack())
            throw fail(videoDropped ? VIDEO_MESSAGES.unsupported : VIDEO_MESSAGES.failed)
        }
        const onAbort = () => { conversion.cancel().catch(() => { }) }
        if (signal) {
            if (signal.aborted) throw Object.assign(new Error('cancelled'), { name: 'AbortError' })
            signal.addEventListener('abort', onAbort, { once: true })
        }
        conversion.onProgress = (p) => onProgress && onProgress(Math.min(0.99, p))
        try {
            await conversion.execute()
        } finally {
            if (signal) signal.removeEventListener('abort', onAbort)
        }
        if (signal && signal.aborted) throw Object.assign(new Error('cancelled'), { name: 'AbortError' })

        const blob = new Blob([target.buffer], { type: 'video/mp4' })
        const duration = await input.computeDuration()
        const poster = await posterFrame(track, CanvasSink, width, height, duration)
        onProgress && onProgress(1)
        return { blob, poster, duration: Math.min(duration, MAX_VIDEO_SECONDS), width, height }
    } catch (error) {
        if (error.friendly || error.name === 'AbortError') throw error
        if (signal && signal.aborted) throw Object.assign(new Error('cancelled'), { name: 'AbortError' })
        console.error(error)
        throw fail(VIDEO_MESSAGES.failed)
    } finally {
        input.dispose()
    }
}

// a frame at 0.5 s (or the middle of very short clips) as the cover picture
const posterFrame = async (track, CanvasSink, width, height, duration) => {
    const sink = new CanvasSink(track, { width, height, fit: 'fill' })
    const at = Math.min(0.5, Math.max(0, duration / 2))
    const frame = (await sink.getCanvas(at)) || (await sink.getCanvas(0))
    if (!frame) throw fail(VIDEO_MESSAGES.failed)
    const canvas = frame.canvas
    if (typeof canvas.convertToBlob === 'function') return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.85 })
    return new Promise((resolve, reject) => canvas.toBlob(b => b ? resolve(b) : reject(fail(VIDEO_MESSAGES.failed)), 'image/jpeg', 0.85))
}

export const formatBytes = (bytes) => bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`

export const formatSeconds = (seconds) => {
    const s = Math.round(seconds || 0)
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
